import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  type ComponentProps,
} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { marked } from "marked";
import {
  Button,
  Card,
  Text,
  Textarea,
  Spinner,
  Tooltip,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuGroup,
  MenuGroupHeader,
  MenuDivider,
  makeStyles,
  tokens,
  mergeClasses,
} from "@fluentui/react-components";
import {
  Send16Regular,
  Sparkle20Regular,
  Add16Regular,
  Clock16Regular,
  Folder16Regular,
  Chat16Regular,
  ChevronDown12Regular,
  ChevronRight12Regular,
  MailArrowForward16Regular,
  BrainCircuit20Regular,
  Play16Regular,
  CheckmarkCircle16Filled,
} from "@fluentui/react-icons";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { getEmailContext, insertContent } from "@/office/mail-context";
import { getProjects, getThreads, getThreadHistory } from "@/services/api";
import {
  connectAssistant,
  sendAssistantMessage,
  joinThread,
  getSocket,
} from "@/services/socket";
import type {
  Project,
  EmailContext,
  AssistantThread,
  ThreadsGroupedByProject,
  ThreadHistoryMessage,
  ThinkingState,
  ThoughtTimelineItem,
} from "@/types";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDuration(ms: number): string {
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },

  /* Header / thread selector */
  header: {
    padding: "8px 12px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  headerBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flex: 1,
    minWidth: 0,
    cursor: "pointer",
    padding: "4px 6px",
    borderRadius: "6px",
    border: "none",
    background: "transparent",
    textAlign: "left",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  headerIcon: {
    flexShrink: 0,
    width: "24px",
    height: "24px",
    borderRadius: "6px",
    background: "linear-gradient(135deg, #42FFC6, #E5FFC2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  headerTitle: {
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  headerSub: {
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },

  /* Email context banner */
  contextCard: {
    margin: "0 12px",
    padding: "8px 10px",
    backgroundColor: "#F5F3FF",
    border: "1px solid #E0DAFB",
    flexShrink: 0,
  },
  contextLabel: {
    fontWeight: 700,
    color: "#6366F1",
    marginBottom: "2px",
  },
  contextItem: {
    color: tokens.colorNeutralForeground2,
    lineHeight: 1.4,
  },

  /* Messages */
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: "8px 12px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  msgUser: {
    alignSelf: "flex-end",
    maxWidth: "90%",
    background: "linear-gradient(135deg, #6366F1, #8B5CF6)",
    color: "#fff",
    padding: "8px 10px",
    borderRadius: "12px 12px 4px 12px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    lineHeight: 1.45,
  },
  msgAi: {
    alignSelf: "flex-start",
    maxWidth: "90%",
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    padding: "8px 10px",
    borderRadius: "12px 12px 12px 4px",
    wordBreak: "break-word",
    lineHeight: 1.45,
    "& p": {
      margin: 0,
      whiteSpace: "pre-wrap",
      "&:not(:last-child)": { marginBottom: "6px" },
    },
    "& strong": { fontWeight: 600 },
    "& em": { fontStyle: "italic" },
    "& ul, & ol": {
      margin: "4px 0",
      paddingLeft: "18px",
    },
    "& li": { lineHeight: 1.5 },
    "& code": {
      backgroundColor: tokens.colorNeutralBackground1,
      padding: "1px 4px",
      borderRadius: "3px",
      fontSize: "0.85em",
    },
    "& pre": {
      backgroundColor: tokens.colorNeutralBackground1,
      padding: "6px 8px",
      borderRadius: "4px",
      overflowX: "auto",
      margin: "4px 0",
      "& code": {
        backgroundColor: "transparent",
        padding: 0,
      },
    },
    "& a": {
      color: "#6366F1",
      textDecoration: "underline",
    },
  },
  msgAiWrap: {
    alignSelf: "flex-start",
    maxWidth: "90%",
    position: "relative",
    "&:hover > button": { opacity: 1 },
  },
  addToEmailBtn: {
    position: "absolute",
    bottom: "-2px",
    right: "-2px",
    opacity: 0,
    transitionProperty: "opacity",
    transitionDuration: "150ms",
    minWidth: 0,
    padding: "2px 6px",
    borderRadius: "6px",
    height: "22px",
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    boxShadow: `0 1px 4px ${tokens.colorNeutralShadowAmbient}`,
    zIndex: 1,
  },

  /* Thinking indicator (replaces dots-only) */
  thinkingIndicator: {
    alignSelf: "flex-start",
    maxWidth: "90%",
    backgroundColor: tokens.colorNeutralBackground3,
    padding: "8px 12px",
    borderRadius: "12px 12px 12px 4px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  thinkingRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  thinkingText: {
    color: tokens.colorNeutralForeground2,
  },
  dotsRow: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    paddingLeft: "2px",
  },
  dot: {
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    backgroundColor: tokens.colorNeutralForeground3,
    animationName: {
      "0%, 80%, 100%": { opacity: 0.3 },
      "40%": { opacity: 1 },
    },
    animationDuration: "1.2s",
    animationIterationCount: "infinite",
  },

  /* Thought timeline (collapsible above AI messages) */
  thoughtTimeline: {
    marginBottom: "4px",
  },
  thoughtHeader: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
    padding: "2px 4px",
    borderRadius: "4px",
    border: "none",
    background: "transparent",
    color: tokens.colorNeutralForeground3,
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  thoughtSteps: {
    paddingLeft: "8px",
    marginTop: "2px",
    borderLeft: `2px solid ${tokens.colorNeutralStroke2}`,
  },
  thoughtStep: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "1px 0",
  },

  /* Legacy typing (fallback if no thinking state) */
  typing: {
    alignSelf: "flex-start",
    maxWidth: "90%",
    backgroundColor: tokens.colorNeutralBackground3,
    padding: "8px 14px",
    borderRadius: "12px 12px 12px 4px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },

  emptyState: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: tokens.colorNeutralForeground3,
  },

  /* Input */
  inputArea: {
    padding: "8px 12px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  inputRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: "6px",
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: "8px",
    padding: "6px 8px",
  },

  /* Menu item active */
  menuItemActive: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
  menuTimeLabel: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface ChatMessage {
  id: string;
  sender: "user" | "ai";
  content: string;
  timestamp: Date;
  thought?: boolean;
  thoughtHistory?: ThoughtTimelineItem[];
  thoughtDuration?: number;
}

interface RecentChat {
  threadId: string;
  title: string;
  projectId: string | null;
  projectName: string;
  updatedAt?: Date;
}

interface ProjectGroup {
  projectId: string | null;
  projectName: string;
  threads: RecentChat[];
}

const MAX_RECENT = 5;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function ChatPanel() {
  const styles = useStyles();
  const { user } = useAuth();

  // Thread & message state
  const [activeThreadId, setActiveThreadId] = useState<string | undefined>();
  const [scopedProjectId, setScopedProjectId] = useState<string | null>(null);
  const [localMessages, setLocalMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  const [emailContext, setEmailContext] = useState<EmailContext | null>(null);

  // Thinking UI state
  const [thinkingState, setThinkingState] = useState<ThinkingState>({ isThinking: false });
  const [thinkingStartTime, setThinkingStartTime] = useState<number | null>(null);
  const [thinkingHistory, setThinkingHistory] = useState<ThoughtTimelineItem[]>([]);

  // Thought timeline expand/collapse per message
  const [expandedThoughts, setExpandedThoughts] = useState<Set<string>>(new Set());

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Load email context from Office.js
  useEffect(() => {
    getEmailContext()
      .then(setEmailContext)
      .catch(() => {});
  }, []);

  // ------ Data fetching ------
  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: () => getProjects(user!.accessToken),
    enabled: !!user,
  });

  // Fetch general threads + per-project threads
  const { data: generalThreadGroups = [] } = useQuery({
    queryKey: ["threads", "general"],
    queryFn: () => getThreads(user!.accessToken),
    enabled: !!user,
  });

  const { data: projectThreadResults = [] } = useQuery({
    queryKey: ["threads", "projects", projects.map((p) => p.id)],
    queryFn: async () => {
      const results = await Promise.all(
        projects.map((p) => getThreads(user!.accessToken, p.id))
      );
      return results.flat();
    },
    enabled: !!user && projects.length > 0,
  });

  // Fetch history for active thread
  const { data: historyData, isLoading: isLoadingHistory } = useQuery({
    queryKey: ["threadHistory", activeThreadId],
    queryFn: () => getThreadHistory(user!.accessToken, activeThreadId!),
    enabled: !!user && !!activeThreadId && !activeThreadId.startsWith("temp-"),
  });

  // ------ Build thread lists ------
  const { recentChats, projectGroups } = useMemo(() => {
    const allChats: RecentChat[] = [];
    const groups: ProjectGroup[] = [];

    // General threads
    for (const group of generalThreadGroups) {
      const g: ProjectGroup = {
        projectId: null,
        projectName: "General",
        threads: [],
      };
      for (const t of group.threads) {
        const chat: RecentChat = {
          threadId: t.id,
          title: t.title || "New Conversation",
          projectId: null,
          projectName: "General",
          updatedAt: t.updatedAt ? new Date(t.updatedAt) : undefined,
        };
        allChats.push(chat);
        g.threads.push(chat);
      }
      if (g.threads.length) groups.push(g);
    }

    // Project threads
    for (const group of projectThreadResults) {
      const pName = group.projectName || "Unknown Project";
      const pId = group.projectId;
      const existing = groups.find((g) => g.projectId === pId);
      const target: ProjectGroup = existing || {
        projectId: pId,
        projectName: pName,
        threads: [],
      };

      for (const t of group.threads) {
        const chat: RecentChat = {
          threadId: t.id,
          title: t.title || "New Conversation",
          projectId: pId,
          projectName: pName,
          updatedAt: t.updatedAt ? new Date(t.updatedAt) : undefined,
        };
        allChats.push(chat);
        target.threads.push(chat);
      }

      if (!existing && target.threads.length) groups.push(target);
    }

    // Sort
    allChats.sort((a, b) => {
      if (!a.updatedAt && !b.updatedAt) return 0;
      if (!a.updatedAt) return 1;
      if (!b.updatedAt) return -1;
      return b.updatedAt.getTime() - a.updatedAt.getTime();
    });
    for (const g of groups) {
      g.threads.sort((a, b) => {
        if (!a.updatedAt && !b.updatedAt) return 0;
        if (!a.updatedAt) return 1;
        if (!b.updatedAt) return -1;
        return b.updatedAt.getTime() - a.updatedAt.getTime();
      });
    }

    return { recentChats: allChats.slice(0, MAX_RECENT), projectGroups: groups };
  }, [generalThreadGroups, projectThreadResults]);

  const currentChat = useMemo(() => {
    if (!activeThreadId) return null;
    for (const g of projectGroups) {
      const found = g.threads.find((c) => c.threadId === activeThreadId);
      if (found) return found;
    }
    return null;
  }, [activeThreadId, projectGroups]);

  // ------ Combine history + local messages ------
  const displayMessages = useMemo(() => {
    if (!activeThreadId) return localMessages;

    const historyMsgs: ChatMessage[] = (historyData?.messages || []).map(
      (m) => ({
        id: m.msg_uuid,
        sender: m.role === "human" ? ("user" as const) : ("ai" as const),
        content: m.content,
        timestamp: new Date(m.message_at),
      })
    );

    const historyIds = new Set(historyMsgs.map((m) => m.id));
    const historyKeys = new Set(
      historyMsgs.map((m) => `${m.sender}:${m.content.trim().slice(0, 100)}`)
    );

    const newLocal = localMessages.filter((m) => {
      if (historyIds.has(m.id)) return false;
      if (m.id.startsWith("local-")) {
        const key = `${m.sender}:${m.content.trim().slice(0, 100)}`;
        if (historyKeys.has(key)) return false;
      }
      return true;
    });

    return [...historyMsgs, ...newLocal];
  }, [activeThreadId, historyData, localMessages]);

  // ------ Auto-scroll ------
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [displayMessages, isTyping, thinkingState.isThinking]);

  // ------ WebSocket setup ------
  useEffect(() => {
    if (!user) return;

    let socket: ReturnType<typeof connectAssistant>;
    try {
      socket = connectAssistant(user.accessToken);
    } catch {
      return;
    }

    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    if (socket.connected) setIsConnected(true);

    const handleChunk = (payload: {
      threadId: string;
      chunk: string;
      messageId: string;
    }) => {
      if (
        payload.threadId !== activeThreadId &&
        !activeThreadId?.startsWith("temp-")
      )
        return;

      // When chunks start arriving, clear thinking state
      setThinkingState({ isThinking: false });
      setIsTyping(false);
      setLocalMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === payload.messageId);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = {
            ...updated[idx],
            content: updated[idx].content + payload.chunk,
          };
          return updated;
        }
        return [
          ...prev,
          {
            id: payload.messageId,
            sender: "ai" as const,
            content: payload.chunk,
            timestamp: new Date(),
          },
        ];
      });
    };

    const handleComplete = (payload: {
      threadId: string;
      messageId: string;
      aiText?: string;
      text?: string;
      thought?: boolean;
      thoughtHistory?: ThoughtTimelineItem[];
      thoughtDuration?: number;
    }) => {
      setIsTyping(false);
      setIsSending(false);
      setThinkingState({ isThinking: false });

      const text = payload.aiText || payload.text || "";
      if (!text) return;

      // Capture thought data from the current session
      const msgThought = payload.thought || false;
      const msgThoughtHistory = payload.thoughtHistory || (thinkingHistory.length > 0 ? thinkingHistory : undefined);
      const msgThoughtDuration = payload.thoughtDuration || (thinkingStartTime ? Date.now() - thinkingStartTime : undefined);

      setLocalMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === payload.messageId);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = {
            ...updated[idx],
            content: text,
            thought: msgThought,
            thoughtHistory: msgThoughtHistory,
            thoughtDuration: msgThoughtDuration,
          };
          return updated;
        }
        return [
          ...prev,
          {
            id: payload.messageId,
            sender: "ai" as const,
            content: text,
            timestamp: new Date(),
            thought: msgThought,
            thoughtHistory: msgThoughtHistory,
            thoughtDuration: msgThoughtDuration,
          },
        ];
      });

      // Reset thinking history for next message
      setThinkingHistory([]);
      setThinkingStartTime(null);
    };

    const handleError = () => {
      setIsTyping(false);
      setIsSending(false);
      setThinkingState({ isThinking: false });
      setThinkingHistory([]);
      setThinkingStartTime(null);
    };

    const handleThreadCreated = (payload: {
      threadId: string;
      optimisticThreadId: string;
    }) => {
      if (activeThreadId === payload.optimisticThreadId) {
        setActiveThreadId(payload.threadId);
        joinThread(payload.threadId);
      }
    };

    const handleTitleUpdated = (payload: {
      threadId: string;
      title: string;
    }) => {
      // Title updates are handled by refetching thread lists
      // (the query will auto-refetch on focus)
    };

    // --- Thinking event handlers ---
    const handleThinkingStart = (payload: {
      threadId: string;
      toolName: string;
      toolDescription: string;
    }) => {
      if (
        payload.threadId !== activeThreadId &&
        !activeThreadId?.startsWith("temp-")
      )
        return;

      const now = Date.now();
      if (!thinkingStartTime) {
        setThinkingStartTime(now);
      }
      setThinkingState({
        isThinking: true,
        toolName: payload.toolName,
        toolDescription: payload.toolDescription,
        progressMessage: payload.toolDescription,
      });
      setIsTyping(false);
      setThinkingHistory((prev) => [
        ...prev,
        {
          type: "tool_start",
          toolName: payload.toolName,
          message: payload.toolDescription,
          timestamp: now,
        },
      ]);
    };

    const handleThinkingProgress = (payload: {
      threadId: string;
      message: string;
    }) => {
      if (
        payload.threadId !== activeThreadId &&
        !activeThreadId?.startsWith("temp-")
      )
        return;

      setThinkingState((prev) => ({
        ...prev,
        progressMessage: payload.message,
      }));
    };

    const handleThinkingComplete = (payload: {
      threadId: string;
      result: string;
    }) => {
      if (
        payload.threadId !== activeThreadId &&
        !activeThreadId?.startsWith("temp-")
      )
        return;

      setThinkingHistory((prev) => {
        // Find the last tool_start without a matching tool_complete and mark it
        const updated = [...prev];
        for (let i = updated.length - 1; i >= 0; i--) {
          if (updated[i].type === "tool_start") {
            updated.push({
              type: "tool_complete",
              toolName: updated[i].toolName,
              message: `${updated[i].toolName} completed`,
              timestamp: Date.now(),
            });
            break;
          }
        }
        return updated;
      });

      // Don't clear thinkingState yet — wait for chunk or complete
      setThinkingState((prev) => ({
        ...prev,
        progressMessage: "Processing results...",
      }));
    };

    const handleKeepalive = (payload: {
      threadId: string;
      message: string;
    }) => {
      if (
        payload.threadId !== activeThreadId &&
        !activeThreadId?.startsWith("temp-")
      )
        return;

      setThinkingState((prev) => ({
        ...prev,
        isThinking: true,
        progressMessage: payload.message,
      }));
    };

    socket.on("assistant:ai-response-chunk", handleChunk);
    socket.on("assistant:ai-response-complete", handleComplete);
    socket.on("assistant:chat-error", handleError);
    socket.on("assistant:thread-created", handleThreadCreated);
    socket.on("assistant:thread-title-updated", handleTitleUpdated);
    socket.on("assistant:thinking-start", handleThinkingStart);
    socket.on("assistant:thinking-progress", handleThinkingProgress);
    socket.on("assistant:thinking-complete", handleThinkingComplete);
    socket.on("assistant:keepalive", handleKeepalive);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("assistant:ai-response-chunk", handleChunk);
      socket.off("assistant:ai-response-complete", handleComplete);
      socket.off("assistant:chat-error", handleError);
      socket.off("assistant:thread-created", handleThreadCreated);
      socket.off("assistant:thread-title-updated", handleTitleUpdated);
      socket.off("assistant:thinking-start", handleThinkingStart);
      socket.off("assistant:thinking-progress", handleThinkingProgress);
      socket.off("assistant:thinking-complete", handleThinkingComplete);
      socket.off("assistant:keepalive", handleKeepalive);
    };
  }, [user, activeThreadId]);

  // ------ Handlers ------
  const handleSend = useCallback(() => {
    if (!input.trim() || isSending || !isConnected) return;

    const trimmed = input.trim();
    setInput("");
    setIsSending(true);
    setIsTyping(true);
    setThinkingState({ isThinking: false });
    setThinkingHistory([]);
    setThinkingStartTime(null);

    const userMsg: ChatMessage = {
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      sender: "user",
      content: trimmed,
      timestamp: new Date(),
    };
    setLocalMessages((prev) => [...prev, userMsg]);

    // Build emailContext payload (separate from message, injected server-side as system context)
    const emailCtx =
      emailContext &&
      (emailContext.to.length > 0 || emailContext.subject || emailContext.body)
        ? {
            from: emailContext.from || undefined,
            to: emailContext.to,
            cc: emailContext.cc.length > 0 ? emailContext.cc : undefined,
            subject: emailContext.subject || undefined,
            body: emailContext.body || undefined,
            itemType: emailContext.itemType,
          }
        : undefined;

    try {
      if (activeThreadId && !activeThreadId.startsWith("temp-")) {
        sendAssistantMessage({
          threadId: activeThreadId,
          message: trimmed,
          source: "outlook-addin-chat",
          emailContext: emailCtx,
        });
      } else {
        const optimisticId = `temp-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2)}`;
        setActiveThreadId(optimisticId);
        sendAssistantMessage({
          createThread: true,
          message: trimmed,
          projectId: scopedProjectId || undefined,
          optimisticThreadId: optimisticId,
          source: "outlook-addin-chat",
          emailContext: emailCtx,
        });
      }
    } catch {
      setIsTyping(false);
      setIsSending(false);
      setLocalMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
      setInput(trimmed);
    }
  }, [input, isSending, isConnected, activeThreadId, scopedProjectId, emailContext]);

  const handleSelectChat = useCallback((chat: RecentChat) => {
    setActiveThreadId(chat.threadId);
    setScopedProjectId(chat.projectId);
    setLocalMessages([]);
    setIsTyping(false);
    setThinkingState({ isThinking: false });
    joinThread(chat.threadId);
  }, []);

  const handleNewChat = useCallback(
    (projectId?: string | null, projectName?: string) => {
      setActiveThreadId(undefined);
      setScopedProjectId(projectId ?? null);
      setLocalMessages([]);
      setIsTyping(false);
      setThinkingState({ isThinking: false });
    },
    []
  );

  const handleAddToEmail = useCallback(async (markdown: string) => {
    try {
      const html = await marked.parse(markdown, { breaks: true });
      await insertContent(html);
    } catch {
      // Not in compose mode or Office.js unavailable — silently ignore
    }
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleThoughtExpanded = useCallback((messageId: string) => {
    setExpandedThoughts((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  }, []);

  const formatTimeAgo = (date?: Date) => {
    if (!date) return "";
    const diff = Date.now() - date.getTime();
    const mins = Math.floor(diff / 60_000);
    const hours = Math.floor(diff / 3_600_000);
    const days = Math.floor(diff / 86_400_000);
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m`;
    if (hours < 24) return `${hours}h`;
    return `${days}d`;
  };

  const scopedProject = useMemo(() => {
    if (!scopedProjectId) return null;
    return projects.find((p) => p.id === scopedProjectId) ?? null;
  }, [scopedProjectId, projects]);

  const displayTitle = currentChat?.title || "New Chat";
  const displayProject =
    currentChat?.projectName ||
    scopedProject?.name ||
    "General Chat";

  const showThinkingIndicator = isTyping || thinkingState.isThinking;
  const thinkingDisplayText =
    thinkingState.progressMessage ||
    thinkingState.toolDescription ||
    "Thinking...";

  // ------ Render ------
  return (
    <div className={styles.root}>
      {/* ---- Header with thread selector ---- */}
      <div className={styles.header}>
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <button className={styles.headerBtn}>
              <div className={styles.headerIcon}>
                <Sparkle20Regular style={{ color: "#000", width: 14, height: 14 }} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Text size={200} className={styles.headerTitle}>
                    {displayTitle}
                  </Text>
                  <ChevronDown12Regular
                    style={{ color: tokens.colorNeutralForeground3, flexShrink: 0 }}
                  />
                </div>
                <div className={styles.headerSub}>
                  {currentChat?.projectId || scopedProjectId ? (
                    <>
                      <Folder16Regular style={{ width: 10, height: 10 }} />
                      <Text size={100}>{displayProject}</Text>
                    </>
                  ) : (
                    <>
                      <Chat16Regular style={{ width: 10, height: 10 }} />
                      <Text size={100}>{displayProject}</Text>
                    </>
                  )}
                </div>
              </div>
            </button>
          </MenuTrigger>

          <MenuPopover style={{ maxHeight: 320, overflowY: "auto" }}>
            <MenuList>
              {/* Start New Chat (general / unscoped) */}
              <MenuItem
                icon={<Add16Regular style={{ color: "#6366F1" }} />}
                onClick={() => handleNewChat(null)}
              >
                <Text size={200} weight="semibold">
                  Start New Chat
                </Text>
              </MenuItem>

              <MenuDivider />

              {/* Recent Chats */}
              {recentChats.length > 0 && (
                <MenuGroup>
                  <MenuGroupHeader>
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <Clock16Regular style={{ width: 12, height: 12 }} />
                      Recent ({recentChats.length})
                    </span>
                  </MenuGroupHeader>
                  {recentChats.map((chat) => (
                    <MenuItem
                      key={chat.threadId}
                      onClick={() => handleSelectChat(chat)}
                      className={
                        activeThreadId === chat.threadId
                          ? styles.menuItemActive
                          : undefined
                      }
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          width: "100%",
                        }}
                      >
                        {chat.projectId ? (
                          <Folder16Regular
                            style={{
                              width: 12,
                              height: 12,
                              color: tokens.colorNeutralForeground3,
                              flexShrink: 0,
                            }}
                          />
                        ) : (
                          <Chat16Regular
                            style={{
                              width: 12,
                              height: 12,
                              color: tokens.colorNeutralForeground3,
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text
                            size={200}
                            style={{
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {chat.title}
                          </Text>
                          <Text
                            size={100}
                            style={{ color: tokens.colorNeutralForeground3 }}
                          >
                            {chat.projectName}
                            {chat.updatedAt &&
                              ` \u00B7 ${formatTimeAgo(chat.updatedAt)}`}
                          </Text>
                        </div>
                      </div>
                    </MenuItem>
                  ))}
                </MenuGroup>
              )}

              {recentChats.length > 0 && <MenuDivider />}

              {/* All Chats grouped by project */}
              <MenuGroup>
                <MenuGroupHeader>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <Folder16Regular style={{ width: 12, height: 12 }} />
                    All Chats
                  </span>
                </MenuGroupHeader>
                {projectGroups.length > 0 ? (
                  projectGroups.map((group) => (
                    <Menu key={group.projectId || "general"}>
                      <MenuTrigger disableButtonEnhancement>
                        <MenuItem>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              width: "100%",
                            }}
                          >
                            <Folder16Regular
                              style={{
                                width: 12,
                                height: 12,
                                color: tokens.colorNeutralForeground3,
                                flexShrink: 0,
                              }}
                            />
                            <Text
                              size={200}
                              style={{
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {group.projectName}
                            </Text>
                            <Text
                              size={100}
                              className={styles.menuTimeLabel}
                            >
                              {group.threads.length}
                            </Text>
                          </div>
                        </MenuItem>
                      </MenuTrigger>
                      <MenuPopover
                        style={{ maxHeight: 240, overflowY: "auto" }}
                      >
                        <MenuList>
                          {group.projectId && (
                            <>
                              <MenuItem
                                icon={
                                  <Add16Regular
                                    style={{ color: "#6366F1", width: 12, height: 12 }}
                                  />
                                }
                                onClick={() =>
                                  handleNewChat(group.projectId, group.projectName)
                                }
                              >
                                <Text size={200} weight="semibold">
                                  New Conversation
                                </Text>
                              </MenuItem>
                              <MenuDivider />
                            </>
                          )}
                          {group.threads.map((chat) => (
                            <MenuItem
                              key={chat.threadId}
                              onClick={() => handleSelectChat(chat)}
                              className={
                                activeThreadId === chat.threadId
                                  ? styles.menuItemActive
                                  : undefined
                              }
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  width: "100%",
                                }}
                              >
                                <Chat16Regular
                                  style={{
                                    width: 12,
                                    height: 12,
                                    color: tokens.colorNeutralForeground3,
                                    flexShrink: 0,
                                  }}
                                />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <Text
                                    size={200}
                                    style={{
                                      display: "block",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {chat.title}
                                  </Text>
                                  {chat.updatedAt && (
                                    <Text
                                      size={100}
                                      style={{
                                        color:
                                          tokens.colorNeutralForeground3,
                                      }}
                                    >
                                      {formatTimeAgo(chat.updatedAt)}
                                    </Text>
                                  )}
                                </div>
                              </div>
                            </MenuItem>
                          ))}
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  ))
                ) : (
                  <MenuItem disabled>
                    <Text
                      size={200}
                      style={{ color: tokens.colorNeutralForeground3 }}
                    >
                      No chats yet
                    </Text>
                  </MenuItem>
                )}
              </MenuGroup>
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>

      {/* ---- Email context banner ---- */}
      {emailContext &&
        (emailContext.to.length > 0 || emailContext.subject) && (
          <Card className={styles.contextCard} size="small">
            <Text className={styles.contextLabel} size={100}>
              EMAIL CONTEXT
            </Text>
            {emailContext.from && emailContext.itemType === "read" && (
              <Text className={styles.contextItem} size={200} block>
                <strong>From:</strong> {emailContext.from}
              </Text>
            )}
            {emailContext.to.length > 0 && (
              <Text className={styles.contextItem} size={200} block>
                <strong>To:</strong> {emailContext.to.join(", ")}
              </Text>
            )}
            {emailContext.subject && (
              <Text className={styles.contextItem} size={200} block>
                <strong>Subject:</strong> {emailContext.subject}
              </Text>
            )}
          </Card>
        )}

      {/* ---- Messages ---- */}
      <div className={styles.messages}>
        {isLoadingHistory && activeThreadId && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "16px 0",
            }}
          >
            <Spinner size="tiny" />
          </div>
        )}

        {displayMessages.map((msg) =>
          msg.sender === "user" ? (
            <div key={msg.id} className={styles.msgUser}>
              <Text size={200}>{msg.content}</Text>
            </div>
          ) : (
            <div key={msg.id} className={styles.msgAiWrap}>
              {/* Thought timeline (collapsible) */}
              {msg.thought && msg.thoughtHistory && msg.thoughtHistory.length > 0 && (
                <div className={styles.thoughtTimeline}>
                  <button
                    className={styles.thoughtHeader}
                    onClick={() => toggleThoughtExpanded(msg.id)}
                  >
                    <BrainCircuit20Regular
                      style={{ width: 14, height: 14, color: tokens.colorNeutralForeground3 }}
                    />
                    <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
                      Thought for {formatDuration(msg.thoughtDuration || 0)}
                    </Text>
                    <ChevronRight12Regular
                      style={{
                        width: 10,
                        height: 10,
                        color: tokens.colorNeutralForeground3,
                        transform: expandedThoughts.has(msg.id)
                          ? "rotate(90deg)"
                          : "none",
                        transition: "transform 150ms",
                      }}
                    />
                  </button>
                  {expandedThoughts.has(msg.id) && (
                    <div className={styles.thoughtSteps}>
                      {msg.thoughtHistory.map((step, i) => (
                        <div key={i} className={styles.thoughtStep}>
                          {step.type === "tool_start" ? (
                            <Play16Regular
                              style={{ width: 10, height: 10, color: "#6366F1", flexShrink: 0 }}
                            />
                          ) : step.type === "tool_complete" ? (
                            <CheckmarkCircle16Filled
                              style={{ width: 10, height: 10, color: "#22C55E", flexShrink: 0 }}
                            />
                          ) : null}
                          <Text
                            size={100}
                            style={{ color: tokens.colorNeutralForeground3 }}
                          >
                            {step.message}
                          </Text>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className={styles.msgAi}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: (props) => (
                      <a {...props} target="_blank" rel="noreferrer" />
                    ),
                  }}
                >
                  {msg.content || ""}
                </ReactMarkdown>
              </div>
              <Tooltip content="Add to email" relationship="label">
                <Button
                  className={styles.addToEmailBtn}
                  appearance="subtle"
                  size="small"
                  icon={<MailArrowForward16Regular style={{ width: 12, height: 12 }} />}
                  onClick={() => handleAddToEmail(msg.content)}
                />
              </Tooltip>
            </div>
          )
        )}

        {/* Thinking indicator (replaces simple dots) */}
        {showThinkingIndicator && (
          <div className={styles.thinkingIndicator}>
            <div className={styles.thinkingRow}>
              <Spinner size="extra-tiny" />
              <Text size={200} className={styles.thinkingText}>
                {thinkingDisplayText}
              </Text>
            </div>
            <div className={styles.dotsRow}>
              <div className={styles.dot} style={{ animationDelay: "0s" }} />
              <div className={styles.dot} style={{ animationDelay: "0.15s" }} />
              <div className={styles.dot} style={{ animationDelay: "0.3s" }} />
            </div>
          </div>
        )}

        {displayMessages.length === 0 && !isLoadingHistory && (
          <div className={styles.emptyState}>
            <Text size={200}>
              {isConnected ? "Start a conversation..." : "Connecting..."}
            </Text>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ---- Input ---- */}
      <div className={styles.inputArea}>
        <div className={styles.inputRow}>
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(_, data) => setInput(data.value)}
            onKeyDown={handleKeyDown}
            placeholder={isConnected ? "Ask anything..." : "Connecting..."}
            disabled={!isConnected || isSending}
            resize="none"
            size="small"
            style={{
              flex: 1,
              minHeight: "24px",
              maxHeight: "80px",
              border: "none",
              background: "transparent",
              padding: 0,
            }}
            textarea={{ style: { minHeight: "24px", padding: "2px 0" } }}
          />
          <Button
            appearance="transparent"
            icon={
              isSending ? (
                <Spinner size="extra-tiny" />
              ) : (
                <Send16Regular />
              )
            }
            size="small"
            onClick={handleSend}
            disabled={!input.trim() || !isConnected || isSending}
            style={{ minWidth: 0, padding: "4px" }}
          />
        </div>
      </div>
    </div>
  );
}
